'use client';

import { useClerk } from '@clerk/clerk-react';
import { resetAnalytics } from '@tagadapay/analytics';
import {
  Activity,
  BarChart3,
  DollarSign,
  Gift,
  Globe2,
  Headset,
  Lock,
  Phone,
  Scale,
  ShieldAlert,
  Repeat,
  Rocket,
  ShoppingCart,
  Store,
  Users,
  Workflow,
} from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

import { brandingConfig, isTagadaPay } from '@/config/branding';
import { usePerm, PERMISSIONS } from '@/hooks/use-perm';
import { useSuperAdmin } from '@/hooks/use-superadmin';
import { usePartnerAdmin } from '@/hooks/use-partner-admin';
import { useSessionClaims } from '@/hooks/use-session-claims';
import { AppHeader } from '@/components/layout/app-header';
import { OrganizationSwitcher } from '@/components/ui/organization-switcher';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarNavLink,
  useSidebar,
} from '@/components/ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { AccountManagerModeBanner } from '@/components/ui/account-manager-mode-banner';
import { AccountWarningBanner } from '@/components/ui/account-warning-banner';
import { useAccountManagerInfo } from '@/lib/api/account';
import { useToBePaidCount, useUnsyncedPaidCount, useStorefrontSyncCapability } from '@/lib/api/orders';
import { useStoresSearch } from '@/lib/api/stores';
import { config } from '@/lib/config';
import { preloadI18nForPath } from '@/lib/i18n-preload';
import { PUBLIC_ROUTES, STORE_ROUTES } from '@/config/router';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { CrmV3WaitlistBanner } from '@/components/layout/crm-v3-waitlist-banner';
import { isFunnelEditorPath } from '@/components/layout/funnel-editor-path';
import { isGuidedSetupEnabled, isSetupRoute, shouldUseSetupFullscreenLayout } from '@/lib/setupFeatures';
import { QuickStartNavBadge } from '@/components/setup/quick-start/QuickStartNavBadge';
import { cn } from '@/lib/utils';
import { RequestFeatureDialog } from '@/components/feedback/request-feature-dialog';
import { SidebarOrderSignal, SidebarStoreActions } from '@/components/layout/sidebar-nav-premium';
// TEMP: Reports sub-menu hidden for now — see the analytics block in the nav list below.
// import { SidebarAnalyticsEntry } from '@/components/layout/sidebar-analytics-entry';
import { SidebarStoreSearch } from '@/components/layout/sidebar-store-search';
import { SidebarStoresList } from '@/components/layout/sidebar-stores-list';
import { HelpVideoWidget } from '@/components/ui/help-video-widget';
import { useEffectiveBranding } from '@/hooks/use-effective-branding';
import { useReferralEnabled } from '@/hooks/use-referral-enabled';
import { useHasOrgPartner } from '@/hooks/use-reseller-partner';
import { useCloakSanitizer } from '@/lib/cloak-sanitize';

const SidebarController = ({ shouldClose }: { shouldClose: boolean }) => {
  const { setOpen } = useSidebar();
  const prevShouldClose = React.useRef<boolean | null>(null);

  React.useEffect(() => {
    // Initial mount handling
    if (prevShouldClose.current === null) {
      if (shouldClose) {
        setOpen(false);
      }
      prevShouldClose.current = shouldClose;
      return;
    }

    // Change handling
    if (prevShouldClose.current !== shouldClose) {
      if (shouldClose) {
        setOpen(false);
      } else {
        setOpen(true);
      }
      prevShouldClose.current = shouldClose;
    }
  }, [shouldClose, setOpen]);

  return null;
};

interface SidebarLayoutProps {
  children: React.ReactNode;
  isAccountManager?: boolean;
}

const getSidebarStateFromCookie = () => {
  const cookies = document.cookie.split('; ');
  const sidebarCookie = cookies.find((cookie) => cookie.startsWith('sidebar_state='));
  return sidebarCookie ? sidebarCookie.split('=')[1] === 'true' : true;
};

export function SidebarLayout({ children, isAccountManager }: SidebarLayoutProps) {
  const { t } = useTranslation(['navigation', 'common']);
  const location = useLocation();
  const [searchParams] = useSearchParams();

  React.useEffect(() => {
    preloadI18nForPath(location.pathname);
  }, [location.pathname]);

  const { orgRoutes, navigate: appNavigate, orgSlug } = useOrgNavigate();
  const { can, isAdmin, isLoaded: permLoaded } = usePerm();
  const { isSuperAdmin } = useSuperAdmin();
  const { isPartnerAdmin, whitelabelOwnership } = usePartnerAdmin();
  const { isRxStaff } = useSessionClaims();

  const { signOut } = useClerk();
  const [sidebarDefaultOpen] = React.useState(getSidebarStateFromCookie);
  const [storeSearch, setStoreSearch] = React.useState('');
  const [isFeatureRequestOpen, setIsFeatureRequestOpen] = React.useState(false);
  const displayReferral = useReferralEnabled();
  const showRequestFeature = isTagadaPay || brandingConfig.features?.showRequestFeature === true;
  const branding = useEffectiveBranding();
  const hasOrgPartner = useHasOrgPartner();
  const cloak = useCloakSanitizer();

  const { data: toBePaidCount } = useToBePaidCount();
  const { data: syncCapability } = useStorefrontSyncCapability();
  const canSyncOrders = syncCapability?.hasEnabledStorefront === true;
  const { data: unsyncedPaidCount } = useUnsyncedPaidCount(canSyncOrders);
  const { data: accountManager, isLoading: isAccountManagerLoading } = useAccountManagerInfo();

  const canViewStores = can(PERMISSIONS.STORES_VIEW);
  const { data: storesData, isLoading: isStoresLoading } = useStoresSearch(
    {
      search: storeSearch.trim() || undefined,
      limit: 30,
    },
    { enabled: permLoaded && canViewStores },
  );
  const isStoreContext = React.useMemo(() => location.pathname?.includes('/stores/'), [location.pathname]);
  const storeId = React.useMemo(
    () => (isStoreContext ? location.pathname.split('/stores/')[1]?.split('/')[0] : null),
    [isStoreContext, location.pathname],
  );

  const stores = storesData?.stores ?? [];

  const activeStoreItemRef = React.useRef<HTMLLIElement>(null);

  React.useEffect(() => {
    if (!storeId || !activeStoreItemRef.current) return;
    activeStoreItemRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [storeId]);

  const handleSignOut = async () => {
    try {
      // Clear analytics identity before signing out — otherwise, on a shared machine, the next
      // merchant's events would be attributed to the previous user.
      resetAnalytics();
      await signOut();
      appNavigate(PUBLIC_ROUTES.SIGN_IN);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Menu items with permission requirements
  const allMenuItems = React.useMemo(
    () => [
      ...(isGuidedSetupEnabled()
        ? [
            {
              id: 'setup',
              title: t('setup'),
              href: orgRoutes.SETUP(),
              icon: <Rocket className="h-4 w-4" />,
              permission: PERMISSIONS.STORES_VIEW,
              sideComponent: <QuickStartNavBadge />,
            },
          ]
        : []),
      {
        id: 'analytics',
        title: t('analytics'),
        href: orgRoutes.ANALYTICS(),
        icon: <BarChart3 className="h-4 w-4" />,
        permission: PERMISSIONS.ANALYTICS_VIEW,
      },
      {
        id: 'stores',
        title: t('stores'),
        href: orgRoutes.STORES(),
        icon: <Store className="h-4 w-4" />,
        permission: PERMISSIONS.STORES_VIEW,
      },
      {
        id: 'orders',
        title: t('orders'),
        href: orgRoutes.ORDERS(),
        icon: <ShoppingCart className="h-4 w-4" />,
        permission: PERMISSIONS.ORDERS_VIEW,
        sideComponent: (
          <SidebarOrderSignal
            toBePaid={toBePaidCount?.total ?? undefined}
            unsynced={canSyncOrders ? (unsyncedPaidCount?.total ?? undefined) : undefined}
          />
        ),
      },
      {
        id: 'payments',
        title: t('payments'),
        href: orgRoutes.PAYMENTS(),
        icon: <DollarSign className="h-4 w-4" />,
        permission: PERMISSIONS.PAYMENTS_VIEW,
      },
      {
        id: 'disputes',
        title: t('disputes'),
        href: orgRoutes.DISPUTES(),
        icon: <Scale className="h-4 w-4" />,
        permission: PERMISSIONS.PAYMENTS_VIEW,
      },
      {
        id: 'subscriptions',
        title: t('subscriptions'),
        href: orgRoutes.SUBSCRIPTIONS(),
        icon: <Repeat className="h-4 w-4" />,
        permission: PERMISSIONS.SUBSCRIPTIONS_VIEW,
      },
      {
        id: 'customers',
        title: t('customers'),
        href: orgRoutes.CUSTOMERS(),
        icon: <Users className="h-4 w-4" />,
        permission: PERMISSIONS.CUSTOMERS_VIEW,
      },
      {
        id: 'payment-flows',
        title: t('paymentFlows'),
        href: orgRoutes.PAYMENT_FLOWS(),
        icon: <Workflow className="h-4 w-4" />,
        permission: PERMISSIONS.PROCESSORS_VIEW,
      },
      {
        id: 'block-rules',
        title: t('blockRules'),
        href: orgRoutes.BLOCK_RULES(),
        icon: <ShieldAlert className="h-4 w-4" />,
        permission: PERMISSIONS.PROCESSORS_VIEW,
      },
      {
        id: 'domains',
        title: t('domains'),
        href: orgRoutes.DOMAINS(),
        icon: <Globe2 className="h-4 w-4" />,
        permission: PERMISSIONS.STORES_VIEW, // domains tied to stores permission
      },
      {
        id: 'external-requests',
        title: t('externalRequests'),
        href: orgRoutes.EXTERNAL_REQUESTS(),
        icon: <Activity className="h-4 w-4" />,
        permission: PERMISSIONS.STORES_VIEW, // external requests tied to stores permission
      },
    ],
    [t, orgRoutes, toBePaidCount, unsyncedPaidCount, canSyncOrders],
  );

  // Add permission status to menu items (don't filter - show all)
  const menuItems = React.useMemo(
    () =>
      allMenuItems.map((item) => ({
        ...item,
        hasAccess: can(item.permission),
      })),
    [allMenuItems, can],
  );

  const getActiveTab = (pathname: string): string | null => {
    const path = pathname.replace(/\/$/, '') || '/';
    const orgBase = orgRoutes.ANALYTICS().replace(/\/$/, '');

    if (path.includes(orgRoutes.SETUP())) return 'setup';
    if (path.includes(orgRoutes.STORES())) return 'stores';
    if (path.includes(orgRoutes.ORDERS())) return 'orders';
    if (path.includes(orgRoutes.SUBSCRIPTIONS())) return 'subscriptions';
    if (path.includes(orgRoutes.CUSTOMERS())) return 'customers';
    if (path.includes(orgRoutes.PAYMENTS())) return 'payments';
    if (path.includes(orgRoutes.DISPUTES())) return 'disputes';
    if (path.includes(orgRoutes.BLOCK_RULES())) return 'block-rules';
    if (path.includes(orgRoutes.PAYMENT_FLOWS()) || path.includes(orgRoutes.PROCESSORS())) {
      return 'payment-flows';
    }
    if (path.includes(orgRoutes.DOMAINS())) return 'domains';
    if (path.includes(orgRoutes.EXTERNAL_REQUESTS())) return 'external-requests';
    if (path.includes(orgRoutes.REPORTS()) || path === orgBase) return 'analytics';

    return null;
  };

  const path = location.pathname.replace(/\/$/, '');
  const isFunnelEditor = isFunnelEditorPath(path);
  const isSetupPage = isSetupRoute(path);
  const isSetupFullscreen =
    isSetupPage && shouldUseSetupFullscreenLayout(stores.length > 0, searchParams, isStoresLoading);

  const appHeader = (
    <AppHeader
      searchPlaceholder={t('searchPlaceholder')}
      displayReferral={displayReferral}
      isAccountManager={isAccountManager}
      isAdmin={isAdmin}
      isSuperAdmin={isSuperAdmin}
      isPartnerAdmin={isPartnerAdmin}
      isRxStaff={isRxStaff}
      whitelabelOwnership={whitelabelOwnership}
      onSignOut={handleSignOut}
      onRequestFeature={() => setIsFeatureRequestOpen(true)}
      showRequestFeature={showRequestFeature}
      hideSidebarTrigger={isSetupFullscreen}
      hideSearch={isSetupFullscreen}
    />
  );

  if (isSetupFullscreen) {
    return (
      <>
        <div className="flex h-[100dvh] max-h-[100dvh] min-h-0 w-full flex-col overflow-hidden bg-[color-mix(in_srgb,var(--muted)_28%,var(--background))]">
          {appHeader}
          <main className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto p-0">{children}</main>
        </div>
        <RequestFeatureDialog open={isFeatureRequestOpen} onOpenChange={setIsFeatureRequestOpen} />
      </>
    );
  }

  return (
    <SidebarProvider defaultOpen={sidebarDefaultOpen}>
      <SidebarController shouldClose={isFunnelEditor} />
      <div className="flex h-screen w-full overflow-hidden">
        <Sidebar className="border-sidebar-border border-r">
          <SidebarHeader className="border-sidebar-border flex h-16 shrink-0 flex-row items-center border-b p-0 px-2.5">
            <OrganizationSwitcher />
          </SidebarHeader>
          <SidebarContent className="crm-sidebar-content px-2.5 pb-3 pt-2">
            <div className="crm-sidebar-body-grid">
              <nav className="crm-sidebar-main-nav" aria-label="Main navigation">
                <SidebarMenu className="gap-px">
                  {!permLoaded
                    ? Array.from({ length: 7 }, (_, i) => (
                        <SidebarMenuItem key={`nav-skeleton-${i}`}>
                          <div className="bg-muted/40 mx-0.5 h-8 animate-pulse rounded-md" />
                        </SidebarMenuItem>
                      ))
                    : menuItems.map((item) => {
                        const isActive = getActiveTab(location.pathname) === item.id;

                        if (!item.hasAccess) {
                          return (
                            <SidebarMenuItem key={item.title}>
                              <Tooltip delayDuration={0}>
                                <TooltipTrigger asChild>
                                  <div className="w-full">
                                    <SidebarMenuButton className="pointer-events-none cursor-not-allowed opacity-45">
                                      {item.icon}
                                      <span className="flex-1">{item.title}</span>
                                      <Lock className="text-muted-foreground size-3.5" />
                                    </SidebarMenuButton>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="right" className="max-w-[240px]">
                                  <p>{t('noAccessToFeature')}</p>
                                </TooltipContent>
                              </Tooltip>
                            </SidebarMenuItem>
                          );
                        }

                        // TEMP: Reports sub-menu hidden for now.
                        // Disabling this block makes the Analytics item fall through to the
                        // default plain link rendering below (no "Reports" child link).
                        // To restore the Reports sub-menu: re-enable this block AND the
                        // SidebarAnalyticsEntry import at the top of the file.
                        // if (item.id === 'analytics') {
                        //   return (
                        //     <SidebarAnalyticsEntry
                        //       key={item.title}
                        //       analyticsHref={orgRoutes.ANALYTICS()}
                        //       reportsHref={orgRoutes.REPORTS()}
                        //       title={item.title}
                        //       reportsLabel={t('reports')}
                        //       icon={item.icon}
                        //     />
                        //   );
                        // }

                        return (
                          <SidebarMenuItem key={item.title}>
                            <SidebarMenuButton asChild isActive={isActive}>
                              <SidebarNavLink to={item.href}>
                                {item.icon}
                                <span className="flex-1">{item.title}</span>
                                {item.sideComponent}
                              </SidebarNavLink>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        );
                      })}
                </SidebarMenu>
              </nav>

              <div className="crm-sidebar-stores-block" aria-label={t('stores')}>
                {!permLoaded ? (
                  <div className="space-y-2 px-0.5">
                    <div className="bg-muted/60 h-8 animate-pulse rounded-md" />
                    <div className="bg-muted/40 h-6 animate-pulse rounded-md" />
                    <div className="bg-muted/40 h-6 animate-pulse rounded-md" />
                  </div>
                ) : canViewStores ? (
                  <>
                    <div className="crm-sidebar-stores-header">
                      <span className="crm-sidebar-section-label">{t('stores')}</span>
                      <SidebarStoreSearch
                        value={storeSearch}
                        onChange={setStoreSearch}
                        placeholder={t('searchStores')}
                      />
                    </div>

                    <div className="crm-sidebar-stores-list">
                      <SidebarStoresList
                        stores={stores}
                        storeId={storeId}
                        storeSearch={storeSearch}
                        isLoading={isStoresLoading}
                        displayName={cloak}
                        storeRoutesFor={(id) => STORE_ROUTES(orgSlug, id)}
                        labels={{
                          general: t('general'),
                          storefront: t('storefront'),
                          emails: t('emails'),
                          paymentFlows: t('paymentFlows'),
                          integrations: t('integrations'),
                          storeSettings: t('storeSettings'),
                        }}
                        activeStoreItemRef={activeStoreItemRef}
                      />
                    </div>

                    <SidebarStoreActions
                      viewAllHref={orgRoutes.STORES()}
                      createHref={orgRoutes.STORE_CREATE()}
                      viewAllLabel={t('viewAllStores')}
                      addStoreLabel={t('addStore')}
                    />
                  </>
                ) : (
                  <div className="flex items-center gap-2 px-1 py-2 opacity-50">
                    <Lock className="text-muted-foreground size-4" />
                    <span className="text-muted-foreground text-xs">{t('common:noAccess')}</span>
                  </div>
                )}
              </div>
            </div>
          </SidebarContent>
          <SidebarFooter className="crm-sidebar-footer border-sidebar-border shrink-0 border-t px-3 py-2">
            {hasOrgPartner && branding.logo && (
              <div className="px-2 pb-2">
                <img
                  src={branding.logo}
                  alt={branding.appName}
                  className="h-6 w-auto opacity-60 transition-opacity hover:opacity-100"
                />
              </div>
            )}
            {displayReferral && (
              <SidebarNavLink to={orgRoutes.REFERRAL()} className="crm-sidebar-nav-item">
                <Gift className="size-4 shrink-0" />
                <span className="flex-1">{t('earnMoney')}</span>
                <span className="crm-sidebar-count bg-emerald-500/12 text-emerald-700 dark:text-emerald-400">
                  10%
                </span>
              </SidebarNavLink>
            )}
            {branding.bookCallUrl ? (
              <a
                href={branding.bookCallUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="crm-sidebar-nav-item"
              >
                <Phone className="size-4 shrink-0" />
                <span className="flex-1">{t('bookACall')}</span>
              </a>
            ) : isTagadaPay && !hasOrgPartner && !isAccountManagerLoading ? (
              accountManager ? (
                <>
                  <div className="crm-sidebar-nav-item pointer-events-none opacity-80">
                    <Headset className="size-4 shrink-0" />
                    <span className="flex-1 truncate">{accountManager.displayName}</span>
                  </div>
                  {accountManager.callUrl ? (
                    <a
                      href={accountManager.callUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="crm-sidebar-nav-item"
                    >
                      <Phone className="size-4 shrink-0" />
                      <span className="flex-1">{t('bookACall')}</span>
                    </a>
                  ) : null}
                </>
              ) : config.introCallUrl ? (
                <a
                  href={config.introCallUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="crm-sidebar-nav-item"
                >
                  <Phone className="size-4 shrink-0" />
                  <span className="flex-1">{t('bookIntroCall')}</span>
                </a>
              ) : (
                <div className="crm-sidebar-nav-item pointer-events-none opacity-80">
                  <Phone className="size-4 shrink-0" />
                  <span className="flex-1">{t('bookIntroCall')}</span>
                </div>
              )
            ) : null}
          </SidebarFooter>
        </Sidebar>
        <SidebarInset className="flex min-w-0 flex-1 flex-col">
          {appHeader}
          {!isFunnelEditor && <CrmV3WaitlistBanner orgSlug={orgSlug} />}
          <AccountManagerModeBanner />
          <AccountWarningBanner />
          <main
            className={cn(
              'bg-background relative min-h-0 w-full min-w-0 flex-1 overscroll-contain',
              isFunnelEditor && 'flex flex-col overflow-hidden p-0',
              !isFunnelEditor &&
                'overflow-auto px-4 pb-[max(env(safe-area-inset-bottom),1.25rem)] pt-5 lg:px-6 lg:pb-6 lg:pt-6',
            )}
          >
            {children}
          </main>
        </SidebarInset>
      </div>
      <RequestFeatureDialog open={isFeatureRequestOpen} onOpenChange={setIsFeatureRequestOpen} />
      {brandingConfig.features?.showHelpVideos && <HelpVideoWidget />}
    </SidebarProvider>
  );
}
